import React, { useState } from 'react';
import { Box, Card, CardContent, Grid, TextField, Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Typography, FormControl, InputLabel, Select, MenuItem, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import { Download, Calculate, Refresh, AccountBalance, TrendingUp, Payment } from '@mui/icons-material';
import { Restore as RestoreIcon, Edit as EditIcon } from "@mui/icons-material";
import { loanInputData, standardAmortizationData, summaryStats } from '../data/mockData';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import '../styles/AmortizationSchedule.scss';
import '../styles/loanCommon.scss';
import LoanInputParameters from './LoanInputParameters';
import { trackActivity } from '../../../../utils/mixpanel';

const AmortizationSchedule = ({ loanType, onLoanTypeChange }) => {
    const handleItemClicked = (page_type) => {
        trackActivity('Item Clicked', { page_title: 'Fixed Loan', page_type: page_type })
    }

    const [inputData, setInputData] = useState({
        ...loanInputData,
        // Remove lessee from initial state
    });
    const [error, setError] = useState({});
    const [scheduleData, setScheduleData] = useState(standardAmortizationData);
    const [openDialog, setOpenDialog] = useState({modal:false});
    const handleInputChange = (field, value) => {
        setInputData({ ...inputData, [field]: value });
    };

    const onRestErrorKey = (key) => {
        setError((prev) => ({ ...prev, [key]: '' }));
    };

    const handleCalculate = () => {
        alert('Calculation logic will be implemented');
    };

    const handleExport = (format) => {
        alert(`Exporting schedule as ${format}`);
    };

    const handleSaveFixedLoan = () => {
        
        alert('Prepayment added successfully');
        setOpenDialog(false);
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
        }).format(amount);
    };

    return (
        <Box className="amortization-schedule-container">
            {/* Summary Cards */}
            <Grid container spacing={3} className="summary-cards">
                <Grid size={{ md: 3, sm: 6, xs: 12 }}>
                    <Card className="summary-card">
                        <CardContent>
                            <Box className="card-header">
                                <AccountBalance className="card-icon" style={{ color: '#3b82f6' }} />
                                <Typography variant="h6" className="card-title">Total Loan</Typography>
                            </Box>
                            <Typography variant="h4" className="card-value">
                                {formatCurrency(summaryStats.totalLoanAmount)}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid size={{ md: 3, sm: 6, xs: 12 }}>
                    <Card className="summary-card">
                        <CardContent>
                            <Box className="card-header">
                                <TrendingUp className="card-icon" style={{ color: '#10b981' }} />
                                <Typography variant="h6" className="card-title">Total Interest</Typography>
                            </Box>
                            <Typography variant="h4" className="card-value">
                                {formatCurrency(summaryStats.totalInterestPaid)}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid size={{ md: 3, sm: 6, xs: 12 }}>
                    <Card className="summary-card">
                        <CardContent>
                            <Box className="card-header">
                                <Payment className="card-icon" style={{ color: '#f59e0b' }} />
                                <Typography variant="h6" className="card-title">Monthly Payment</Typography>
                            </Box>
                            <Typography variant="h4" className="card-value">
                                {formatCurrency(summaryStats.averageMonthlyPayment)}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid size={{ md: 3, sm: 6, xs: 12 }}>
                    <Card className="summary-card">
                        <CardContent>
                            <Box className="card-header">
                                <Calculate className="card-icon" style={{ color: '#8b5cf6' }} />
                                <Typography variant="h6" className="card-title">Total Payments</Typography>
                            </Box>
                            <Typography variant="h4" className="card-value">
                                {formatCurrency(summaryStats.totalPayments)}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Input Form - WITHOUT LESSEE */}
            <Card className="input-form-card">
                <CardContent>
                    <Box className="table-header">
                        <Typography className='loan-heading'>Loan Input Parameters</Typography>
                        <Box className="export-buttons">
                            <Tooltip arrow title="Revision History"><RestoreIcon className="revision-history" sx={{ cursor: 'pointer' }} onClick={() => handleItemClicked('RevisionHistoryIcon')} fontSize='small' color='primary' /></Tooltip>
                            <Tooltip arrow title="Edit"><EditIcon sx={{ cursor: 'pointer' }} onClick={() => { setOpenDialog({modal:true}); handleItemClicked('EditIcon') }} fontSize='small' color='primary' /></Tooltip>
                        </Box>
                    </Box>
                    <Grid container spacing={3}>
                        <LoanInputParameters inputData={inputData} handleInputChange={handleInputChange} loanType={loanType} onLoanTypeChange={onLoanTypeChange} type={'view'} />
                    </Grid>
                </CardContent>
            </Card>

            {/* Amortization Table */}
            <Card className="table-card">
                <CardContent>
                    <Box className="table-header">
                        <Typography className='loan-heading'>Amortization Schedule</Typography>
                        <Box className="export-buttons">
                            <Button startIcon={<ArrowDownwardIcon />} variant="outlined" size='small' onClick={() => handleItemClicked('Export Button')}> Export </Button>
                        </Box>
                    </Box>
                    <TableContainer component={Paper} className="lease-detail-table">
                        <Table className="mui-table-format" stickyHeader>
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{minWidth: '130px'}}>Month</TableCell>
                                    <TableCell sx={{minWidth: '130px'}}>Date</TableCell>
                                    <TableCell align="left" sx={{minWidth: '130px'}}>Days (30/360)</TableCell>
                                    <TableCell align="left" sx={{minWidth: '100px'}}>Actual Days</TableCell>
                                    <TableCell align="left" sx={{minWidth: '100px'}}>Debt Service</TableCell>
                                    <TableCell align="left" sx={{minWidth: '100px'}}>Interest</TableCell>
                                    <TableCell align="left" sx={{minWidth: '200px'}}>Principal Repayment</TableCell>
                                    <TableCell align="left" sx={{minWidth: '200px'}}>Principal Outstanding</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {scheduleData.map((row) => (
                                    <TableRow key={row.month} hover>
                                        <TableCell>{row.month}</TableCell>
                                        <TableCell>{row.date}</TableCell>
                                        <TableCell align="left">{row.days30_360}</TableCell>
                                        <TableCell align="left">{row.actualDays}</TableCell>
                                        <TableCell align="left">{formatCurrency(row.debtService)}</TableCell>
                                        <TableCell align="left">{formatCurrency(row.interest)}</TableCell>
                                        <TableCell align="left">{formatCurrency(row.principalRepayment)}</TableCell>
                                        <TableCell align="left" className="outstanding-balance">
                                            {formatCurrency(row.principalOutstanding)}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </CardContent>
            </Card>

            {openDialog?.modal ? <Dialog aria-labelledby="customized-dialog-title" className='add-lease-dialog' maxWidth='md' open={true}>
                <DialogTitle className='dialog-header'>Edit Fixed Load</DialogTitle>
                <DialogContent dividers>
                    <Grid container spacing={2}>
                        <LoanInputParameters type={'edit'} inputData={inputData} loanType={loanType} onLoanTypeChange={onLoanTypeChange} onRestErrorKey={onRestErrorKey} error={error}/>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => { setOpenDialog({modal:false}); handleItemClicked('Cancel Button') }} size='small'>Cancel</Button>
                    <Button onClick={() => { handleSaveFixedLoan(); handleItemClicked('Save Button') }} variant="contained" color="primary" size='small'>Save</Button>
                </DialogActions>
            </Dialog> : null}
        </Box>
    );
};

export default AmortizationSchedule;

